// Translations for backend error codes (see internal/pseudonymizer/handlers/error_codes.go).
// Keyed by the stable "code" field the backend sends alongside its English "error"
// string; a code missing here (or missing entirely) falls back to "generic".
const apiErrors = {
  generic: "Something went wrong. Please try again.",

  INVALID_REQUEST_BODY: "The request could not be read. Please try again.",
  ALL_FIELDS_REQUIRED: "Email, name, institution, and password are all required.",
  INVALID_EMAIL_FORMAT: "Please enter a valid email address.",
  EMAIL_DOMAIN_NOT_ALLOWED: "Email domain not allowed. Please use your institutional email address.",
  PI_EMAIL_REQUIRED: "A principal investigator's email is required to register without an invitation.",
  REGISTRATION_BY_INVITE_ONLY:
    "Registration is by invitation only. Please use the invitation link from your project administrator, or contact them to be invited.",
  USER_EXISTS_LOGIN_REDIRECT:
    "An account with this email already exists. Please log in and request project access from your dashboard.",
  USER_EXISTS_ACTIVE_INVITE:
    "An account with this email already exists and is active. Please log in and request project access from your dashboard.",
  USER_ALREADY_EXISTS: "An account with this email already exists.",
  CREATE_USER_FAILED: "We couldn't create your account. Please try again.",
  INVITATION_INVALID: "This invitation link is invalid or has expired.",
  INVITATION_FOR_EMAIL_ONLY: "This invitation is for {{email}} only.",
  INVITATION_PROCESSING_FAILED: "We couldn't process this invitation. Please try again.",
  REGISTRATION_FAILED: "We couldn't complete your registration. Please try again.",
  ASSIGN_USER_TO_PROJECT_FAILED: "We couldn't add you to the project. Please try again.",

  LOGIN_RATE_LIMITED: "Too many failed login attempts. Please try again in {{retryAfterSeconds}} seconds.",
  INVALID_CREDENTIALS: "Incorrect email or password.",
  USER_NOT_INVITED:
    "This account does not exist in the system. Access requires an invitation — please contact your project administrator.",
  SESSION_CREATE_FAILED: "We couldn't start your session. Please try again.",
  INVALID_2FA_TOKEN: "Invalid two-factor code.",
  REFRESH_TOKEN_REQUIRED: "A refresh token is required.",
  REFRESH_TOKEN_INVALID: "Your session is invalid or has expired. Please log in again.",
  AUTHENTICATION_REQUIRED: "Please log in to continue.",
  LOGOUT_FAILED: "We couldn't log you out. Please try again.",
  EMAIL_CODE_REQUIRED: "Email and verification code are required.",
  EMAIL_REQUIRED: "Email is required.",
  VERIFICATION_SEND_FAILED: "We couldn't send the verification code. Please try again.",
  PASSWORD_RESET_RATE_LIMITED: "Too many password reset requests. Please try again after {{resetTime}}.",
  RESET_TOKEN_REQUIRED: "A reset token and new password are required.",
  CURRENT_NEW_PASSWORD_REQUIRED: "Current password and new password are required.",
  "2FA_TOKEN_REQUIRED": "A two-factor code is required.",
  EMAIL_CODE_EXPIRED: "This verification code has expired. Please request a new one.",
  EMAIL_CODE_TOO_MANY_ATTEMPTS: "Too many failed attempts. Please request a new verification code.",
  EMAIL_CODE_INVALID: "Invalid verification code.",
  EMAIL_CODE_NOT_FOUND: "No verification code was found for this email. Please request a new one.",
  RESET_TOKEN_INVALID: "This password reset link is invalid.",
  RESET_TOKEN_USED: "This password reset link has already been used.",
  RESET_TOKEN_EXPIRED: "This password reset link has expired. Please request a new one.",
  PASSWORD_REUSED: "This password has been used recently. Please choose a different password.",
  CURRENT_PASSWORD_INCORRECT: "Your current password is incorrect.",
  NEW_PASSWORD_SAME_AS_OLD: "Your new password must be different from your current password.",
  USER_NOT_FOUND: "User not found.",

  "2FA_STATUS_FAILED": "We couldn't check your two-factor status. Please try again.",
  INVALID_PASSWORD: "Incorrect password.",
  "2FA_PASSWORD_REQUIRED": "Please enter your password to set up two-factor authentication.",
  "2FA_SESSION_EXPIRED": "Your session has expired. Please enter your password to continue.",
  "2FA_SECRET_GENERATION_FAILED": "We couldn't generate a two-factor setup code. Please try again.",
  "2FA_SECRET_TOKEN_REQUIRED": "A setup secret and two-factor code are required.",
  USER_NOT_FOUND_IN_CONTEXT: "Your session could not be verified. Please log in again.",

  PROJECT_ARCHIVED:
    "This project is archived and read-only. A PI or org manager must restore it (project Settings → Project Controls) before anything in it can change.",
  PROJECT_STATE_VERIFY_FAILED: "We couldn't verify the project's state. Please try again.",
  PROJECT_MANAGE_ACCESS_REQUIRED: "You do not have management access to this project.",

  MANAGER_CANNOT_CREATE_REQUEST: "The manager role cannot create data requests.",
  PROJECT_ID_REQUIRED: "A project is required.",
  PROJECT_ACCESS_CHECK_FAILED: "We couldn't verify your project access. Please try again.",
  ACCESS_DENIED_TO_PROJECT: "You don't have access to this project.",
  TITLE_DESCRIPTION_REQUIRED: "A title and description are required.",
  SELECTED_COLUMNS_REQUIRED: "Please select at least one column.",
  DATASET_ACCESS_VALIDATE_FAILED: "We couldn't verify access to this dataset. Please try again.",
  DATASET_NOT_FOUND_OR_DENIED: "This dataset was not found, or you don't have access to it.",
  INVALID_DATASET_EXPIRY: "The expiry date is not valid.",
  DATASET_EXPIRY_IN_PAST: "The expiry date must be in the future.",
  SELECTED_COLUMN_INVALID: 'The column "{{column}}" does not exist in this dataset.',
  CREATE_DATA_REQUEST_FAILED: "We couldn't create your data request. Please try again.",
  LIST_DATA_REQUESTS_FAILED: "We couldn't load your data requests. Please try again.",
  INVALID_REQUEST_ID: "Invalid request ID.",
  DATA_REQUEST_NOT_FOUND: "This data request was not found.",
  GET_DATA_REQUEST_FAILED: "We couldn't load this data request. Please try again.",
  VERIFY_ACCESS_FAILED: "We couldn't verify your access. Please try again.",
  ACCESS_DENIED: "You don't have access to this.",
  CANCEL_NOT_PENDING: "Only pending requests can be cancelled.",
  CANCEL_DATA_REQUEST_FAILED: "We couldn't cancel this data request. Please try again.",
  EDIT_NOT_PENDING: "Only pending requests can be edited.",
  NO_FIELDS_TO_UPDATE: "No changes to save.",
  UPDATE_DATA_REQUEST_FAILED: "We couldn't update this data request. Please try again.",
  MANAGER_CANNOT_COMMENT: "The manager role cannot comment on data requests.",
  COMMENT_REQUIRED: "A comment is required.",
  COMMENTS_NOT_ALLOWED_STATUS: "Comments are only allowed on pending and approved requests.",
  CREATE_COMMENT_FAILED: "We couldn't post your comment. Please try again.",
  GET_COMMENTS_FAILED: "We couldn't load the comments. Please try again.",

  INVALID_PROJECT_ID: "Invalid project ID.",
  INVALID_INVITATION_ID: "Invalid invitation ID.",
  ROLE_REQUIRED: "A role is required.",
  INVITEE_EMAIL_DOMAIN_NOT_ALLOWED:
    "Email domain not allowed. Invitees must use an institutional email address.",
  NO_ORGANIZATION_CONTEXT: "Your account is not associated with an organization.",
  INVITATION_TOKEN_REQUIRED: "An invitation token is required.",
  VERIFY_PERMISSIONS_FAILED: "We couldn't verify your permissions. Please try again.",
  INVITATIONS_OWN_PROJECTS_ONLY: "You can only view invitations for your own projects.",
  RETRIEVE_INVITATIONS_FAILED: "We couldn't load the invitations. Please try again.",

  NOTIFICATIONS_LIST_FAILED: "We couldn't load your notifications. Please try again.",
  NOTIFICATIONS_UNREAD_COUNT_FAILED: "We couldn't load your unread notification count. Please try again.",
  NOTIFICATION_INVALID_ID: "Invalid notification ID.",
  NOTIFICATION_NOT_FOUND_OR_READ: "This notification was not found, or has already been read.",
  NOTIFICATION_MARK_READ_FAILED: "We couldn't mark this notification as read. Please try again.",
  NOTIFICATIONS_MARK_ALL_READ_FAILED: "We couldn't mark all notifications as read. Please try again.",
  NOTIFICATION_NOT_FOUND_OR_DISMISSED: "This notification was not found, or has already been dismissed.",
  NOTIFICATION_DISMISS_FAILED: "We couldn't dismiss this notification. Please try again.",

  RETRIEVE_ACCESS_REQUESTS_FAILED: "We couldn't load the access requests. Please try again.",

  PASSWORD_REQUIREMENTS_NOT_MET: "Password does not meet security requirements.",
  PASSWORD_FORBIDDEN_PATTERN: "Password contains a forbidden pattern. Please choose a stronger password.",
  PASSWORD_BREACHED: "This password has appeared in a known data breach. Please choose a different password.",
  passwordRequirementsIntro: "Password does not meet security requirements:",
  passwordRequirements: {
    MIN_LENGTH: "at least {{minLength}} characters",
    UPPERCASE: "at least one uppercase letter",
    LOWERCASE: "at least one lowercase letter",
    DIGIT: "at least one number",
    SPECIAL_CHAR: "at least one special character ({{specialChars}})",
    AVOID_PATTERNS: "avoid patterns like: {{patterns}}",
    NOT_BREACHED: "a password that has not been exposed in a known data breach",
  },
};

export default apiErrors;
